import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePersistedState } from "@/hooks/usePersistedState";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  Business,
} from "@mui/icons-material";
import {
  Button,
  Card,
  InputField,
  LoadingSpinner,
  ConfirmDialog,
  ErrorMessage,
  PageHeader,
  FiltersCard,
} from "@/components/ui";
import { enterpriseService } from "@/services/enterprise.service";
import { useToastStore } from "@/store/toastStore";

export function EnterprisesPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastStore();
  const [searchTerm, setSearchTerm] = usePersistedState("enterprises.searchTerm", "");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Récupérer les entreprises
  const {
    data: enterprises = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["enterprises", searchTerm],
    queryFn: () => enterpriseService.getAll(true, searchTerm || undefined),
  });

  // Mutation pour supprimer
  const deleteMutation = useMutation({
    mutationFn: (id: string) => enterpriseService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enterprises"] });
      success("Entreprise supprimée avec succès");
      setDeleteId(null);
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          "Erreur lors de la suppression de l'entreprise"
      );
      setDeleteId(null);
    },
  });

  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  };

  if (isLoading) return <LoadingSpinner message="Chargement des entreprises..." />;
  if (error) {
    const err = error as { response?: { status?: number; data?: { error?: { message?: string }; message?: string } }; message?: string };
    const apiMsg =
      err?.response?.data?.error?.message ||
      err?.response?.data?.message;
    const status = err?.response?.status;
    const fallback = "Erreur lors du chargement des entreprises.";
    const statusSuffix = status ? ` (HTTP ${status})` : "";
    const message = apiMsg
      ? `${apiMsg}${statusSuffix}`
      : err?.message
        ? `${err.message}${statusSuffix}`.trim() || fallback
        : fallback;
    if (import.meta.env.DEV) {
      console.error("[EnterprisesPage] Load error:", err);
    }
    return (
      <ErrorMessage
        title="Erreur de chargement"
        message={message}
        onRetry={() => refetch()}
        retryLabel="Réessayer"
      />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Entreprises"
        subtitle="Gérez les entreprises partenaires"
        action={
          <Button
            variant="primary"
            startIcon={<Add />}
            onClick={() => navigate("/enterprises/new")}
          >
            Nouvelle entreprise
          </Button>
        }
      />

      <FiltersCard>
        <InputField
          placeholder="Rechercher une entreprise..."
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          icon={<Search />}
          iconPosition="start"
          sx={{ "& .MuiOutlinedInput-root": { height: 40 } }}
        />
      </FiltersCard>

      {/* Tableau */}
      <Card padding="none">
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>SIRET / TVA</TableCell>
              <TableCell>Ville</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {enterprises.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Business sx={{ fontSize: 40, color: "text.secondary" }} />
                    <Typography color="text.secondary">
                      Aucune entreprise trouvée
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              enterprises.map((enterprise) => (
                <TableRow key={enterprise.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {enterprise.name}
                    </Typography>
                    {enterprise.email && (
                      <Typography variant="caption" color="text.secondary">
                        {enterprise.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {enterprise.siret && (
                        <Typography variant="caption">SIRET: {enterprise.siret}</Typography>
                      )}
                      {enterprise.vatNumber && (
                        <Typography variant="caption">TVA: {enterprise.vatNumber}</Typography>
                      )}
                      {!enterprise.siret && !enterprise.vatNumber && "-"}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {enterprise.city ? (
                      <Typography variant="body2">
                        {enterprise.city} {enterprise.postalCode && `(${enterprise.postalCode})`}
                      </Typography>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {enterprise.contactPerson && (
                        <Typography variant="body2">{enterprise.contactPerson}</Typography>
                      )}
                      {enterprise.phone && (
                        <Typography variant="caption" color="text.secondary">
                          {enterprise.phone}
                        </Typography>
                      )}
                      {!enterprise.contactPerson && !enterprise.phone && "-"}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={enterprise.isActive ? "Actif" : "Inactif"}
                      color={enterprise.isActive ? "success" : "default"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifier">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/enterprises/${enterprise.id}/edit`)}
                        color="primary"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteId(enterprise.id)}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Card>

      {/* Dialog de confirmation de suppression */}
      <ConfirmDialog
        open={!!deleteId}
        title="Supprimer l'entreprise"
        message="Êtes-vous sûr de vouloir supprimer cette entreprise ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
